import BENEFICIARY_SUBJECT_TYPE_KEY from '@Domain/Subjects/BeneficiarySubjectTypeKey';
import buildSubjectListPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectListPath';
import Container from '@Guidelines/Component/Container';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import InterviewUploadContent from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewUpload/InterviewUploadContent';
import InterviewsNotifications from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewsNotifications';
import useSubjectAddressParams from '@UI/Pages/Beneficiaries/Hooks/Params/useSubjectAddressParams';
import type { InterviewDetailsSource } from '@UI/Pages/Beneficiaries/Hooks/useRetrieveInterviewDetail/resolveInterviewDetail';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import SubjectBreadcrumb from '@UI/Pages/Beneficiaries/Shared/SubjectBreadcrumb';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const BREADCRUMB_ITEMS = [{ label: "Envoi de l'enregistrement" }] as const;

type RouteParams = {
	readonly interview?: string;
	readonly localInterview?: string;
};

/**
 * Mounted from `…/interviews/:interview/upload` (an interview the server knows) and
 * `…/local-interviews/:localInterview/upload` (one born on this device). An invalid dossier address
 * redirects to the beneficiary list, an invalid interview segment back to the dossier. A local
 * identifier is an opaque device id: an unknown one is the content's own "not found".
 */
const InterviewUpload: React.FC = () => {
	const navigate = useNavigate();
	const { interview, localInterview } = useParams<RouteParams>();
	const { subject } = useSubjectAddressParams();

	const source = React.useMemo<InterviewDetailsSource | null>(() => {
		if (interview !== undefined && !Number.isNaN(Number(interview))) {
			return { type: 'server', interviewId: Number(interview) };
		}

		if (localInterview !== undefined && localInterview.length > 0) {
			return { type: 'local', localInterviewId: localInterview };
		}

		return null;
	}, [interview, localInterview]);

	React.useEffect(() => {
		if (subject === null) {
			navigate(buildSubjectListPath(BENEFICIARY_SUBJECT_TYPE_KEY));
			return;
		}

		if (source === null) {
			navigate(buildSubjectPath(subject));
		}
	}, [subject, source, navigate]);

	return (
		<Container>
			<ConditionalLoader data={subject}>
				{(address) => (
					<React.Fragment>
						<SubjectBreadcrumb subject={address} items={BREADCRUMB_ITEMS} />
						<ConditionalLoader data={source}>
							{(validSource) => (
								<InterviewUploadContent subject={address} source={validSource} />
							)}
						</ConditionalLoader>
					</React.Fragment>
				)}
			</ConditionalLoader>
			<InterviewsNotifications />
		</Container>
	);
};

export default InterviewUpload;
